/**
 * Office Service - Helpers that use the Office Common API (Office.context).
 * These are separate from Word.run() operations in word-service.ts.
 *
 * getFilePropertiesAsync makes a fresh call into the Office host on each invocation,
 * so it can reflect document URL changes (e.g. after rename + save) without reloading the add-in.
 * This is in contrast to the synchronous Office.context.document.url property, which is a
 * snapshot captured at add-in load time.
 */

/**
 * Read the currently selected text via the Office Common API.
 * Faster than Word.run() + context.sync() because it is a single direct call
 * with no request-context setup or batching overhead.
 * Returns an empty string when nothing is selected (collapsed cursor).
 */
export function getSelectedDataAsync(): Promise<string> {
  return new Promise((resolve) => {
    Office.context.document.getSelectedDataAsync(Office.CoercionType.Text, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve((result.value as string) ?? '')
      } else {
        resolve('')
      }
    })
  })
}

/**
 * Multicast dispatcher for DocumentSelectionChanged.
 *
 * Word Online and some desktop hosts behave poorly when multiple addHandlerAsync
 * calls register separate handlers (second registration can fail silently or break
 * the first). All React hooks share one stable Office handler and fan out locally.
 */
const selectionChangeHandlers = new Set<() => void>()

function dispatchSelectionChange() {
  for (const handler of selectionChangeHandlers) {
    try {
      handler()
    } catch {
      // One subscriber threw - keep notifying the rest.
    }
  }
}

let officeHandlerRegistered = false
let registerInFlight = false

function registerOfficeSelectionHandler() {
  if (officeHandlerRegistered || registerInFlight || selectionChangeHandlers.size === 0) {
    return
  }

  registerInFlight = true
  Office.context.document.addHandlerAsync(
    Office.EventType.DocumentSelectionChanged,
    dispatchSelectionChange,
    (result) => {
      registerInFlight = false
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        officeHandlerRegistered = true
        // All subscribers may have unsubscribed while registration was in-flight.
        if (selectionChangeHandlers.size === 0) {
          unregisterOfficeSelectionHandler()
        }
        return
      }

      // Registration failed (host not ready, transient error) - retry on next subscribe.
      officeHandlerRegistered = false
    }
  )
}

function unregisterOfficeSelectionHandler() {
  if (!officeHandlerRegistered) {
    return
  }

  Office.context.document.removeHandlerAsync(
    Office.EventType.DocumentSelectionChanged,
    { handler: dispatchSelectionChange },
    (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        officeHandlerRegistered = false
        // A new subscriber might have arrived while unregister was in-flight.
        registerOfficeSelectionHandler()
      }
    }
  )
}

/**
 * Subscribe to Word selection changes via the Office Common API.
 * Fires on every selection change (cursor moves, mouse highlights, keyboard selection).
 * Returns an unsubscribe function - call it on cleanup to remove the handler.
 */
export function subscribeToSelectionChanges(handler: () => void): () => void {
  selectionChangeHandlers.add(handler)
  registerOfficeSelectionHandler()

  return () => {
    selectionChangeHandlers.delete(handler)
    if (selectionChangeHandlers.size === 0) {
      unregisterOfficeSelectionHandler()
    }
  }
}

/** Test-only reset so unit tests do not leak module state between cases. */
export function resetSelectionChangeSubscriptionForTests() {
  selectionChangeHandlers.clear()
  officeHandlerRegistered = false
  registerInFlight = false
}

/**
 * Get the current document's location.
 * Uses getFilePropertiesAsync, which queries the Office host on each call.
 * The URL is:
 *  - a local file path on Word Desktop (e.g. C:\\Users\\...\\doc.docx)
 *  - a SharePoint/OneDrive HTTPS URL on Word Online (e.g. https://tenant.sharepoint.com/.../doc.docx)
 * Returns null when the document is unsaved or the host call fails.
 */
export async function getDocumentUrl(): Promise<string | null> {
  return new Promise((resolve) => {
    Office.context.document.getFilePropertiesAsync((result) => {
      const succeeded = result.status === Office.AsyncResultStatus.Succeeded
      resolve(succeeded && result.value.url ? result.value.url : null)
    })
  })
}

/**
 * Get the current document name from its URL.
 * SharePoint URLs are percent-encoded, so we decode the extracted filename.
 * Returns null when url is null/empty (unsaved document) - callers handle the display fallback.
 */
export async function getDocumentName(): Promise<string | null> {
  const url = await getDocumentUrl()
  const raw = url?.split(/[/\\]/).filter(Boolean).pop()
  if (!raw) return null

  try {
    return decodeURIComponent(raw)
  } catch {
    return raw
  }
}
